import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle2, XCircle, RefreshCw, Loader2, ShieldAlert } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

/**
 * Admin → Sanity / Rollback Safeguard.
 * Hit this page after every deploy or whenever a user reports "old behavior is back" —
 * it surfaces a rollback in seconds via static code-pattern checks + DB smoke checks.
 */
export default function AdminSanity() {
  const [billing, setBilling] = useState(null);
  const [dbCheck, setDbCheck] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [billingRes, dbRes] = await Promise.all([
        api.get('/admin/sanity/billing'),
        api.get('/admin/sanity/db'),
      ]);
      setBilling(billingRes.data);
      setDbCheck(dbRes.data);
      setLastChecked(new Date());
      const passed = billingRes.data.passed;
      const total = billingRes.data.total;
      if (passed === total) {
        toast.success(`All ${total} sanity checks passed`);
      } else {
        toast.warning(`${total - passed} of ${total} sanity checks FAILED — codebase may have rolled back`);
      }
    } catch (err) {
      toast.error('Failed to load sanity data: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6 max-w-5xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldAlert className="h-6 w-6 text-amber-500" />
              Rollback Safeguard
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Quick post-deploy health check. Run this after every release or if
              users start reporting "old behavior is back".
            </p>
            {lastChecked && (
              <p className="text-xs text-slate-400 mt-1">
                Last checked: {lastChecked.toLocaleString()}
              </p>
            )}
          </div>
          <Button onClick={refresh} disabled={loading} data-testid="sanity-refresh-btn" className="bg-blue-600 hover:bg-blue-700">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Re-run checks
          </Button>
        </div>

        {billing && (
          <Card>
            <CardHeader><CardTitle>Build summary</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-500">Cache version</p>
                <p className="font-mono text-lg font-semibold" data-testid="sanity-cache-version">
                  {billing.cache_version || 'unknown'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Code-pattern checks</p>
                <p className="text-lg font-semibold">
                  <span className={billing.all_ok ? 'text-emerald-600' : 'text-red-600'} data-testid="sanity-checks-summary">
                    {billing.passed} / {billing.total}
                  </span>{' '}passed
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Status</p>
                {billing.all_ok ? (
                  <Badge className="bg-emerald-100 text-emerald-700 mt-1" data-testid="sanity-status-badge">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> All Green
                  </Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-700 mt-1" data-testid="sanity-status-badge">
                    <XCircle className="h-3 w-3 mr-1" /> Regression Detected
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {billing && (
          <Card>
            <CardHeader><CardTitle>Code-pattern checks</CardTitle></CardHeader>
            <CardContent>
              <ul className="divide-y" data-testid="sanity-checks-list">
                {billing.checks.map((c) => (
                  <li key={c.id} className="flex items-start gap-3 py-2.5" data-testid={`sanity-check-${c.id}`}>
                    {c.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                          : <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />}
                    <div className="flex-1">
                      <p className={c.ok ? 'text-slate-700' : 'text-red-700 font-medium'}>{c.label}</p>
                      <p className="text-[11px] text-slate-400 font-mono">{c.id}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {billing?.sample_parent_summary && (
          <Card>
            <CardHeader><CardTitle className="text-sm">Sample parent billing snapshot</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <p className="text-slate-500 mb-2">
                Parent: <span className="font-mono">{billing.sample_parent_summary.parent_email}</span>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-slate-700 mb-1">Pending Invoices by type</p>
                  <pre className="bg-slate-50 border rounded p-2 text-xs overflow-x-auto">
                    {JSON.stringify(billing.sample_parent_summary.pending_invoices_by_type, null, 2)}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-700 mb-1">Recent Payments by type</p>
                  <pre className="bg-slate-50 border rounded p-2 text-xs overflow-x-auto">
                    {JSON.stringify(billing.sample_parent_summary.recent_payments_by_type, null, 2)}
                  </pre>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {dbCheck && (
          <Card>
            <CardHeader><CardTitle>Database collection counts</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                {Object.entries(dbCheck.counts || {}).map(([k, v]) => (
                  <div key={k} className="border rounded p-3" data-testid={`db-count-${k}`}>
                    <p className="text-xs text-slate-500">{k}</p>
                    <p className="text-lg font-semibold">{v}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
